/**
 * A pricebook service's parts totals, priced by the rules the rows carry.
 *
 * The totals need the markup profiles to resolve a `COST_PLUS` row that names a
 * profile rather than a percentage, and the profiles arrive over the network —
 * so the hook fetches them here rather than making every surface remember to.
 * The summary panel that reads this and the part-row editor that writes it are
 * then quoting the same arithmetic from the same fields.
 */
import { useMemo } from 'react';
import { useMarkupProfiles } from './useDynamicPricing';
import {
  serviceMemberCostBreakdownsFrom,
  serviceMemberCostsFromBreakdowns,
  useServiceMemberBreakdowns,
} from './useServiceMemberCosts';
import {
  computePricebookComponentTotals,
  type PricebookComponentItemCache,
  type PricebookComponentTotals,
} from '@/components/organisms/pricebook/support/pricebook-bundle';
import type { DynamicServicePricingConfig, ServiceComponent } from '@/types/pricebook';

export function useComponentTotals(
  children: ServiceComponent[],
  itemCache: PricebookComponentItemCache,
  servicePrice = 0,
  /**
   * The service's pricing defaults. A row that names no profile of its own
   * falls back to these; omit them and such a row prices at cost, which is what
   * "Cost only · 0% markup" means and is the safer of the two ways to be wrong.
   */
  config?: Pick<
    DynamicServicePricingConfig,
    'materialMarkupProfileId' | 'equipmentMarkupProfileId' | 'useSameMarkupProfile'
  >,
  /**
   * True on the FLAT-price path, where the typed price is the whole selling
   * price and the parts are only what it costs to deliver. See
   * `PricebookComponentPricingContext.partsAreCostOnly`.
   */
  partsAreCostOnly = false,
  /**
   * True when the parent is a BUNDLE, whose own price is the whole of what it
   * charges — its members are inside that price, never beside it. See
   * `PricebookComponentPricingContext.isBundle`.
   */
  isBundle = false,
): PricebookComponentTotals {
  // `isFetched`, not `data`: an empty list and a list not here yet are
  // different answers for a row naming a profile, and a request that FAILED
  // is not one still in flight — see
  // `PricebookComponentPricingContext.markupProfilesLoaded`. The row editor
  // and the detail card read the same flag, so the summary these totals feed
  // cannot hold a row at $500 while the table beside it holds it at $250.
  const { data: markupProfiles = [], isFetched: markupProfilesLoaded } = useMarkupProfiles();
  // A bundle's SERVICE members cost their labour plus their own parts, and
  // nothing on the member's catalog row says so — see `useServiceMemberCosts`.
  // Resolved here so every surface reading these totals reports the same cost
  // and the same margin, rather than each remembering to ask.
  const serviceMemberBreakdowns = useServiceMemberBreakdowns(children, itemCache);
  const serviceMemberCosts = useMemo(
    () => serviceMemberCostsFromBreakdowns(serviceMemberBreakdowns),
    [serviceMemberBreakdowns],
  );
  const serviceMemberCostBreakdowns = useMemo(
    () => serviceMemberCostBreakdownsFrom(serviceMemberBreakdowns),
    [serviceMemberBreakdowns],
  );

  return useMemo(
    () =>
      computePricebookComponentTotals(children, itemCache, servicePrice, {
        markupProfiles,
        markupProfilesLoaded,
        materialMarkupProfileId: config?.materialMarkupProfileId,
        equipmentMarkupProfileId: config?.equipmentMarkupProfileId,
        useSameMarkupProfile: config?.useSameMarkupProfile,
        partsAreCostOnly,
        isBundle,
        serviceMemberCosts,
        serviceMemberCostBreakdowns,
      }),
    [
      children,
      itemCache,
      servicePrice,
      markupProfiles,
      markupProfilesLoaded,
      config?.materialMarkupProfileId,
      config?.equipmentMarkupProfileId,
      config?.useSameMarkupProfile,
      partsAreCostOnly,
      isBundle,
      serviceMemberCosts,
      serviceMemberCostBreakdowns,
    ],
  );
}
